/**
 * Consent-decision measurement — first-party, no personal data.
 *
 * Records banner funnel events (shown / accepted / rejected / opted out /
 * preference changed / policy applied) to /api/consent/metrics so the
 * opt-in rate can be measured WITHOUT depending on GA4/Clarity/PostHog
 * (which may not be permitted to run yet when the decision happens).
 *
 * What is collected, exactly: { event, policy } — two enum strings — plus a
 * server-side created_at timestamp. NO email, account id, IP address, URL,
 * user agent, cookie, or any identifier is included or stored; the payload
 * is enum-validated server-side and rejected otherwise. This deliberately
 * cannot be used to track a person, so it does not undermine the consent
 * policy it measures.
 *
 * Dedupe: banner_shown and policy_applied fire at most once per browser
 * session (best-effort sessionStorage guard); decision events fire per
 * action. Failures are silent — measurement must never break the banner.
 */

import { getRegionPolicy, type RegionPolicy } from '@/lib/consent-manager'

export type ConsentMetricEvent =
  | 'banner_shown'
  | 'accepted'
  | 'rejected'
  | 'opted_out'
  | 'preference_changed'
  | 'policy_applied'

export interface ConsentMetricPayload {
  event: ConsentMetricEvent
  policy: RegionPolicy
}

const ONCE_PER_SESSION: ReadonlySet<ConsentMetricEvent> = new Set([
  'banner_shown',
  'policy_applied',
])

/** Pure payload builder — the beacon carries these two enum fields only. */
export function buildConsentMetricPayload(
  event: ConsentMetricEvent,
  policy: RegionPolicy
): ConsentMetricPayload {
  return { event, policy }
}

export function recordConsentMetric(event: ConsentMetricEvent): void {
  if (typeof window === 'undefined') return
  try {
    if (ONCE_PER_SESSION.has(event)) {
      const key = `pm_cm_${event}`
      try {
        if (sessionStorage.getItem(key)) return
        sessionStorage.setItem(key, '1')
      } catch {
        // Storage unavailable — send without dedupe rather than lose signal.
      }
    }

    const payload = JSON.stringify(
      buildConsentMetricPayload(event, getRegionPolicy())
    )
    const url = '/api/consent/metrics'

    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(
        url,
        new Blob([payload], { type: 'application/json' })
      )
    } else {
      void fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {})
    }
  } catch {
    // Measurement must never break the consent UX.
  }
}
